import { createAsyncThunk, createEntityAdapter, createSelector, createSlice, EntityId, EntityState, PayloadAction } from '@reduxjs/toolkit'
import { IntegrationGroup } from '@sendhub/integrations/api-client-typescript-axios'
import { integrationsApi } from '../../services/integrations-api'
import { RootState } from '../root'

export const INTEGRATION_GROUPS_FEATURE_KEY = 'integrationGroups'

export interface IntegrationGroupsState extends EntityState<Readonly<IntegrationGroup>> {
  loadingStatus: 'not loaded' | 'loading' | 'loaded' | 'error'
  error?: string | null
}

export const integrationGroupsAdapter = createEntityAdapter<Readonly<IntegrationGroup>>()

/**
 * Export an effect using createAsyncThunk from
 * the Redux Toolkit: https://redux-toolkit.js.org/api/createAsyncThunk
 *
 * e.g.
 * ```
 * import React, { useEffect } from 'react';
 * import { useDispatch } from 'react-redux';
 *
 * // ...
 *
 * const dispatch = useDispatch();
 * useEffect(() => {
 *   dispatch(fetchIntegrationGroups())
 * }, [dispatch]);
 * ```
 */
export const fetchIntegrationGroups = createAsyncThunk('integrationGroups/fetchStatus', async (_, thunkAPI) => {
  return integrationsApi.listIntegrationGroups().then((r) => r.data)
})

export const initialIntegrationGroupsState: IntegrationGroupsState = integrationGroupsAdapter.getInitialState({
  loadingStatus: 'not loaded',
  error: null,
})

export const integrationGroupsSlice = createSlice({
  name: INTEGRATION_GROUPS_FEATURE_KEY,
  initialState: initialIntegrationGroupsState,
  reducers: {
    add: integrationGroupsAdapter.addOne,
    setMany: integrationGroupsAdapter.setMany,
    remove: integrationGroupsAdapter.removeOne,
    // ...
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchIntegrationGroups.pending, (state: IntegrationGroupsState) => {
        state.loadingStatus = 'loading'
      })
      .addCase(fetchIntegrationGroups.fulfilled, (state: IntegrationGroupsState, action: PayloadAction<IntegrationGroup[]>) => {
        integrationGroupsAdapter.setAll(state, action.payload)
        state.loadingStatus = 'loaded'
      })
      .addCase(fetchIntegrationGroups.rejected, (state: IntegrationGroupsState, action) => {
        state.loadingStatus = 'error'
        state.error = action.error.message
      })
  },
})

/*
 * Export reducer for store configuration.
 */
export const integrationGroupsReducer = integrationGroupsSlice.reducer

/*
 * Export action creators to be dispatched. For use with the `useDispatch` hook.
 *
 * e.g.
 * ```
 * import React, { useEffect } from 'react';
 * import { useDispatch } from 'react-redux';
 *
 * // ...
 *
 * const dispatch = useDispatch();
 * useEffect(() => {
 *   dispatch(integrationGroupsActions.add({ id: 1 }))
 * }, [dispatch]);
 * ```
 *
 * See: https://react-redux.js.org/next/api/hooks#usedispatch
 */
export const integrationGroupsActions = integrationGroupsSlice.actions

/*
 * Export selectors to query state. For use with the `useSelector` hook.
 *
 * e.g.
 * ```
 * import { useSelector } from 'react-redux';
 *
 * // ...
 *
 * const entities = useSelector(selectAllIntegrationGroups);
 * ```
 *
 * See: https://react-redux.js.org/next/api/hooks#useselector
 */
const { selectAll, selectEntities, selectById, selectIds } = integrationGroupsAdapter.getSelectors()

export const getIntegrationGroupsState = (rootState: RootState): IntegrationGroupsState => rootState[INTEGRATION_GROUPS_FEATURE_KEY]

export const selectIntegrationGroupById = (id: EntityId) => (state: RootState) => selectById(getIntegrationGroupsState(state), id)
export const selectAllIntegrationGroups = createSelector(getIntegrationGroupsState, selectAll)
export const selectAllIntegrationGroupIds = createSelector(getIntegrationGroupsState, selectIds)
export const selectIntegrationConfigsLoadingStatus = createSelector(getIntegrationGroupsState, (state) => state.loadingStatus)

export const selectIntegrationGroupsEntities = createSelector(getIntegrationGroupsState, selectEntities)
